// The world hex grid: constants, projection, and hex math.
//
// Pointy-top "odd-r" offset grid over the Patterson cylindrical projection.
// Columns wrap east-west (Civ-style); rows do not wrap.
//
//   world units: hex outer radius R = 1, map lies on the XZ plane, +x = east, +z = south, +y = up
//
//        x = 0                                            x = WORLD_W (== x = 0, wraps)
//   z=0  +------------------------------------------------+   row 0     (~88.6 N)
//        |  /\  /\  /\                                     |
//        | |  ||  ||  |   row 0: col centres at √3·(c+0.5)  |
//        |  \/\/\/\/\/\                                    |
//        |   |  ||  ||  | row 1 (odd): shifted +√3/2       |
//        |                                                 |   row 168   (equator)
//        +------------------------------------------------+   row 336   (~88.6 S)
//
// Mirrored exactly by tools/bake/worldciv_bake/grid.py. test/core/grid.test.ts checks both agree.

export const COLS = 512;
export const ROWS = 337;
export const HEX_COUNT = COLS * ROWS;

const SQRT3 = Math.sqrt(3);

/** Horizontal distance between neighbouring hex centres. */
export const HEX_W = SQRT3;

/** Vertical distance between rows. */
export const ROW_H = 1.5;

export const WORLD_W = COLS * HEX_W;
export const WORLD_H = ROWS * ROW_H;

/** World units per radian of longitude (and per unit of Patterson y). */
export const K = WORLD_W / (2 * Math.PI);

const DEG = Math.PI / 180;

// Patterson cylindrical projection coefficients (Patterson, Šavrič, Jenny 2014; d3-geo-projection).
const P1 = 1.0148;
const P2 = 0.23185;
const P3 = -0.14499;
const P4 = 0.02406;
const NEWTON_EPS = 1e-9;
const NEWTON_MAX_STEPS = 30;

/** Patterson forward: latitude (radians) → projected y. */
export function pattersonY(phi: number): number {
  const p2 = phi * phi;
  return phi * (P1 + p2 * p2 * (P2 + p2 * (P3 + P4 * p2)));
}

/** Patterson inverse: projected y → latitude (radians), by Newton iteration. */
export function pattersonPhi(y: number): number {
  let phi = y;
  for (let step = 0; step < NEWTON_MAX_STEPS; step++) {
    const p2 = phi * phi;
    const f = phi * (P1 + p2 * p2 * (P2 + p2 * (P3 + P4 * p2))) - y;
    const df = P1 + p2 * p2 * (5 * P2 + p2 * (7 * P3 + 9 * P4 * p2));
    const delta = f / df;
    phi -= delta;
    if (Math.abs(delta) < NEWTON_EPS) {
      break;
    }
  }
  return phi;
}

export type Vec2 = { x: number; z: number };
export type LonLat = { lon: number; lat: number };

/** Longitude/latitude (degrees) → world XZ. Longitude -180 maps to x = 0. */
export function lonLatToWorld(lon: number, lat: number): Vec2 {
  const x = ((lon + 180) * DEG) * K;
  const z = WORLD_H / 2 - pattersonY(lat * DEG) * K;
  return { x: wrapX(x), z };
}

/** World XZ → longitude/latitude (degrees). */
export function worldToLonLat(x: number, z: number): LonLat {
  const lon = (wrapX(x) / K) / DEG - 180;
  const lat = pattersonPhi((WORLD_H / 2 - z) / K) / DEG;
  return { lon, lat };
}

/** Wrap an x coordinate into [0, WORLD_W). */
export function wrapX(x: number): number {
  const w = x % WORLD_W;
  return w < 0 ? w + WORLD_W : w;
}

/** Wrap a column into [0, COLS). */
export function wrapCol(col: number): number {
  const c = col % COLS;
  return c < 0 ? c + COLS : c;
}

export function hexIndex(col: number, row: number): number {
  return row * COLS + wrapCol(col);
}

export function hexCol(index: number): number {
  return index % COLS;
}

export function hexRow(index: number): number {
  return Math.floor(index / COLS);
}

/** Centre of a hex in world units. */
export function hexCenter(col: number, row: number): Vec2 {
  return { x: HEX_W * (wrapCol(col) + 0.5 + 0.5 * (row & 1)), z: ROW_H * (row + 0.5) };
}

/** Centre of a hex in degrees. */
export function hexLonLat(col: number, row: number): LonLat {
  const c = hexCenter(col, row);
  return worldToLonLat(c.x, c.z);
}

// Directions double as edge ids. Clockwise with +z pointing south (screen-down):
//
//            4 NW   5 NE
//              ___/\___
//     3 W     |        |    0 E
//              ‾‾‾\/‾‾‾
//            2 SW   1 SE
//
// Corner i sits at angle (60·i − 30)° measured from +x toward +z, so edge d runs from corner d to
// corner d+1. Edge d of a hex is edge (d + 3) % 6 of its neighbour in direction d.
export const DIR = { E: 0, SE: 1, SW: 2, W: 3, NW: 4, NE: 5 } as const;
export type Dir = (typeof DIR)[keyof typeof DIR];

// [dcol, drow] per direction, for even rows then odd rows.
const NEIGHBOUR_EVEN: ReadonlyArray<readonly [number, number]> = [[1, 0], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1]];
const NEIGHBOUR_ODD: ReadonlyArray<readonly [number, number]> = [[1, 0], [1, 1], [0, 1], [-1, 0], [0, -1], [1, -1]];

/** Neighbour hex index in direction `dir`, or -1 past the top or bottom row. */
export function neighbour(index: number, dir: number): number {
  const col = hexCol(index);
  const row = hexRow(index);
  const [dc, dr] = (row & 1 ? NEIGHBOUR_ODD : NEIGHBOUR_EVEN)[dir];
  const r = row + dr;
  if (r < 0 || r >= ROWS) {
    return -1;
  }
  return hexIndex(col + dc, r);
}

export function oppositeDir(dir: number): number {
  return (dir + 3) % 6;
}

/** Corner `i` offset from a hex centre, in world units. */
export function cornerOffset(i: number): Vec2 {
  const a = (60 * i - 30) * DEG;
  return { x: Math.cos(a), z: Math.sin(a) };
}

/** Hex containing a world point (wraps x). Uses cube rounding. */
export function hexAtWorld(x: number, z: number): number {
  const px = wrapX(x) - HEX_W / 2;
  const pz = z - ROW_H / 2;

  // Pixel → fractional axial (pointy-top, size 1), then cube round.
  const q = (SQRT3 / 3) * px - pz / 3;
  const r = (2 / 3) * pz;
  let rx = Math.round(q);
  let rz = Math.round(r);
  const ry = Math.round(-q - r);
  const dx = Math.abs(rx - q);
  const dy = Math.abs(ry - (-q - r));
  const dz = Math.abs(rz - r);
  if (dx > dy && dx > dz) {
    rx = -ry - rz;
  } else if (dy <= dz) {
    rz = -rx - ry;
  }

  // Axial → odd-r offset.
  const row = Math.min(ROWS - 1, Math.max(0, rz));
  const col = rx + (rz - (rz & 1)) / 2;
  return hexIndex(col, row);
}

/** Hex containing a lon/lat point. */
export function hexAtLonLat(lon: number, lat: number): number {
  const p = lonLatToWorld(lon, lat);
  return hexAtWorld(p.x, p.z);
}

/** Hex distance on the wrapping grid. */
export function hexDistance(a: number, b: number): number {
  const ar = hexRow(a);
  const br = hexRow(b);
  const aq = hexCol(a) - (ar - (ar & 1)) / 2;
  let best = Infinity;

  // Try the direct path and both wrapped paths.
  for (const shift of [0, COLS, -COLS]) {
    const bq = hexCol(b) + shift - (br - (br & 1)) / 2;
    const dq = aq - bq;
    const dr = ar - br;
    const d = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
    if (d < best) {
      best = d;
    }
  }
  return best;
}
